import type { ListModule } from '../config/constants';
import {
  abandonListCaptures,
  beginListCapture,
  getList,
  finishListCapture,
  setListCaptureState,
  type LocalList,
} from '../data/localDb';
import { useCaptureStore } from '../stores/captureStore';
import { useUiStore } from '../stores/uiStore';
import {
  CAPTURE_EVENTS_PROTOCOL,
  type CaptureAckErrorCode,
  type CaptureBatch,
  type CaptureRuntimeEvent,
} from '../types/captureProtocol';
import type { SearchInput } from '../types/searchInput';
import {
  CaptureEngine,
  migrateCaptureEngineSnapshot,
  type CaptureEngineSnapshot,
  type CapturePhase,
  type CapturePauseReason,
  type CaptureScheduleRow,
  type CaptureStep,
} from './captureEngine';
import {
  createCaptureCountSync,
  type CaptureCountSync,
} from './captureCountSync';
import { sendRuntime, setSync, setSyncConfirmed } from './extensionBridge';
import { emitActivity, type ActivityEventName, type ActivityObject } from './activity';
import { requestListBackup } from './listBackupQueue';
import { speedFactor } from './entitlements';
import { useSessionStore } from '../stores/sessionStore';
import { consumeNewListIntent } from './newListIntent';
import {
  isRunModule,
  useCaptureRunStore,
  type RunBegin,
  type RunStored,
  type RunSync,
} from '../stores/captureRunStore';

export interface StartCaptureRuntimeOptions {
  listId: string;
  userId: string;
  module: ListModule;
  schedule: readonly CaptureScheduleRow[];
  input: SearchInput;
  count?: number;
  language?: string;
  modeMaps?: string;
  /** Intenção exata que originou uma lista nova; consumida só após o run existir no IDB. */
  newListRequestId?: string;
}

export interface CaptureRuntimeScope {
  userId: string;
  module: ListModule;
}

export interface StoredCaptureBatch {
  ackId: string;
  batch: CaptureBatch;
  result: { inserted: number; count: number; added?: RunStored['added'] };
}

interface RuntimeStorage {
  getList: typeof getList;
  beginListCapture: typeof beginListCapture;
  abandonListCaptures: typeof abandonListCaptures;
  finishListCapture: typeof finishListCapture;
  setListCaptureState: typeof setListCaptureState;
}

interface RuntimeBridge {
  sendRuntime: (data: Record<string, unknown>) => void;
  setSync: (data: Record<string, unknown>) => void;
  commitSync: (data: Record<string, unknown>) => Promise<void>;
}

interface RuntimeBackup {
  uploadList: (listId: string) => Promise<unknown>;
}

export interface CaptureRuntimeViewState {
  /** Projeção da identidade persistida; nunca cria ou troca uma execução. */
  runId: string | null;
  /** Projeção da fase persistida; `active` é derivado dela para a UI existente. */
  phase: CapturePhase | null;
  active: boolean;
  module: ListModule;
  count: number;
  /** Módulo ao qual `count` pertence; impede um badge de contaminar outra janela. */
  countModule: ListModule;
  activeListId: string | null;
  pauseReason: CapturePauseReason | null;
  progress: number;
  page: number;
  pages: number;
}

interface RuntimeView {
  update: (patch: Partial<CaptureRuntimeViewState>) => void;
  notify: (kind: 'success' | 'error', message: string) => void;
}

/** Diário por pesquisa da busca em tela cheia (stores/captureRunStore.ts). Só emails/phones. */
export interface RuntimeRunLog {
  begin: (info: RunBegin) => void;
  sync: (info: RunSync) => void;
  stored: (info: RunStored) => void;
  end: (info: {
    runId: string;
    module: 'emails' | 'phones';
    outcome: 'finished' | 'stopped';
    listCount: number;
  }) => void;
}

/** Registro de uso da captura (decisão 172): só o que o motor sabe, nada de termos ou contatos. */
export interface RuntimeActivity {
  emit: (name: ActivityEventName, props?: Record<string, unknown>, obj?: ActivityObject) => void;
  now?: () => number;
}

/** Contadores da execução em memória, para os eventos de etapa e de fim. */
interface RunActivityStats {
  stepStartedAt: number;
  stepFound: number;
  stepFresh: number;
  stepPages: number;
  leadsNew: number;
  pausedAt: number | null;
  acks: Set<string>;
  rejected: Set<string>;
}

export interface CaptureRuntimeDependencies {
  storage: RuntimeStorage;
  bridge: RuntimeBridge;
  backup: RuntimeBackup;
  view: RuntimeView;
  now: () => string;
  createRunId: () => string;
  consumeNewListIntent: typeof consumeNewListIntent;
  countSync?: CaptureCountSync;
  runLog?: RuntimeRunLog;
  activity?: RuntimeActivity;
  /** Fator de velocidade do plano (decisão 299): 1 = "Rápido", 2 = "Normal". */
  speed?: () => number;
}

function createRunId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  // Identidade de correlação, não segredo. O fallback cobre somente browsers sem randomUUID.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

async function withCaptureAccountLock<T>(
  userId: string,
  operation: () => Promise<T>,
): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return operation();
  // Duas abas não podem instalar/projetar runs diferentes fora de ordem. O IDB protege os
  // dados; este lock protege a sequência IDB -> chrome.storage.sync -> navegação.
  return locks.request(`eminer:capture-runtime:${userId}`, operation);
}

const DEFAULT_DEPENDENCIES: CaptureRuntimeDependencies = {
  storage: {
    getList,
    beginListCapture,
    abandonListCaptures,
    finishListCapture,
    setListCaptureState,
  },
  bridge: { sendRuntime, setSync, commitSync: setSyncConfirmed },
  backup: { uploadList: requestListBackup },
  view: {
    update: (patch) => useCaptureStore.setState(patch),
    notify: (kind, message) => useUiStore.getState().pushNotice(kind, message),
  },
  now: () => new Date().toISOString(),
  createRunId,
  consumeNewListIntent,
  runLog: {
    begin: (info) => useCaptureRunStore.getState().begin(info),
    sync: (info) => useCaptureRunStore.getState().sync(info),
    stored: (info) => useCaptureRunStore.getState().stored(info),
    end: (info) => useCaptureRunStore.getState().end(info),
  },
  activity: { emit: emitActivity },
  speed: () => speedFactor(useSessionStore.getState().session),
};

function pauseMessage(reason: CapturePauseReason): string {
  if (reason === 'user_requested') return 'Captura pausada.';
  if (reason === 'email_dns_unavailable') {
    return 'Captura pausada: não foi possível validar os emails porque o DNS está indisponível. Verifique sua conexão e retome a captura.';
  }
  if (reason === 'quota_exceeded') {
    return 'Captura pausada: o armazenamento local está cheio. Libere espaço e retome a captura.';
  }
  if (reason === 'extension_unavailable') {
    return 'Captura pausada: a extensão não confirmou o início. Verifique a extensão e retome a captura.';
  }
  if (reason === 'search_tab_closed') {
    return 'Captura pausada: a aba de busca foi fechada. Reabra a busca e retome a captura.';
  }
  if (reason === 'delivery_failed') {
    return 'Captura pausada: não foi possível entregar os leads ao painel. Retome para tentar novamente.';
  }
  return 'Captura pausada. O painel foi fechado; retome a captura quando estiver pronto.';
}

function isPauseReason(
  code: CaptureAckErrorCode,
): code is Extract<CapturePauseReason, CaptureAckErrorCode> {
  return code === 'email_dns_unavailable' || code === 'quota_exceeded';
}

function buildList(
  options: StartCaptureRuntimeOptions,
  existing: LocalList | undefined,
  captureState: CaptureEngineSnapshot,
  now: string,
): LocalList & { captureState: CaptureEngineSnapshot } {
  return {
    ...(existing ?? {
      id: options.listId,
      userId: options.userId,
      module: options.module,
      count: options.count ?? 0,
      searching: true,
      createdAt: now,
      updatedAt: now,
      backupState: 'synced' as const,
    }),
    id: options.listId,
    userId: options.userId,
    module: options.module,
    keywords: options.input.keywords,
    sites: options.input.sites,
    domains: options.input.domains,
    countries: options.input.country,
    area_code: options.input.area_code,
    locations: options.input.places,
    distance: options.input.distance,
    count: options.count ?? existing?.count ?? 0,
    searching: true,
    updatedAt: now,
    captureState,
  };
}

/** Coordena ACK → persistência do cursor → próxima navegação para uma única lista ativa. */
export class CaptureRuntime {
  private engine: CaptureEngine | null = null;
  /** Dono da lista em execução; o diário da tela cheia é separado por pessoa. */
  private userId = '';
  private readonly countSync: CaptureCountSync;
  private readonly runStats = new Map<string, RunActivityStats>();

  constructor(private readonly dependencies: CaptureRuntimeDependencies = DEFAULT_DEPENDENCIES) {
    this.countSync = dependencies.countSync ?? createCaptureCountSync(dependencies.bridge.setSync);
  }

  async start(options: StartCaptureRuntimeOptions): Promise<void> {
    return withCaptureAccountLock(options.userId, () => this.startLocked(options));
  }

  private async startLocked(options: StartCaptureRuntimeOptions): Promise<void> {
    this.countSync.reset();
    const engine = new CaptureEngine({
      runId: this.dependencies.createRunId(),
      listId: options.listId,
      module: options.module,
      schedule: options.schedule,
      language: options.language,
      phase: 'running',
    });
    const first = engine.next();
    const existing = await this.dependencies.storage.getList(options.listId);
    const now = this.dependencies.now();
    const installed = await this.dependencies.storage.beginListCapture(
      buildList(options, existing, engine.snapshot(), now),
    );
    if (!installed.ok) {
      throw new Error(`Não foi possível instalar a execução local: ${installed.reason ?? 'unknown'}.`);
    }
    this.engine = engine;
    this.userId = options.userId;
    const module = options.module;
    if (isRunModule(module)) {
      this.dependencies.runLog?.begin({
        runId: engine.runId,
        listId: options.listId,
        userId: options.userId,
        module,
        schedule: engine.snapshot().schedule,
        current: engine.peekCurrent()?.i ?? null,
        listCount: options.count ?? existing?.count ?? 0,
        keywords: options.input.keywords,
        sites: options.input.sites,
        areaCode: options.input.area_code?.[0],
      });
    }
    if (options.newListRequestId) {
      // A intenção não some ao receber HTTP 200. Só este ponto prova que a lista nova
      // ganhou identidade/fase duráveis e que um retry não voltará para a lista anterior.
      this.dependencies.consumeNewListIntent(
        options.userId,
        options.module,
        options.newListRequestId,
      );
    }
    for (const supersededId of installed.supersededListIds) {
      void this.dependencies.backup.uploadList(supersededId).catch(() => {
        // A lista substituída continua elegível por dirtyAt; o início da nova não espera rede.
      });
    }

    const view = {
      module: options.module,
      count: options.count ?? existing?.count ?? 0,
      countModule: options.module,
      activeListId: first ? options.listId : null,
      pauseReason: null,
      progress: engine.progress,
      page: first?.position ?? engine.total,
      pages: engine.total,
    } satisfies Partial<CaptureRuntimeViewState>;

    // Os campos viajam juntos e a URL só sai depois de a extensão devolver este snapshot.
    // Ordem de emissão não basta: `chrome.storage.sync.set` confirma de forma assíncrona.
    const routingSnapshot = {
      play: engine.phase === 'running' && first !== null,
      captureTarget: 'panel',
      module: options.module,
      captureListId: options.listId,
      captureRunId: engine.runId,
      ...(options.modeMaps ? { modeMaps: options.modeMaps } : {}),
    };

    try {
      await this.dependencies.bridge.commitSync(routingSnapshot);
    } catch (error) {
      engine.pause('extension_unavailable');
      await this.dependencies.storage.setListCaptureState(options.listId, engine.snapshot());
      this.projectEngine(engine, { ...view, pauseReason: 'extension_unavailable' });
      throw error;
    }

    this.projectEngine(engine, view);
    this.statsFor(engine);
    this.emit('capture.run_started', engine, { steps: engine.total, resumed: false });

    if (first) {
      // O mesmo listId pode ser recapturado. O sinal explícito pertence apenas ao primeiro
      // comando; etapas seguintes e resume não podem zerar o contador da captura em andamento.
      this.sendNavigation(engine, first, true);
    }
    else await this.finish(options.listId, engine, options.count ?? existing?.count ?? 0);
  }

  async restore(listId: string, scope?: CaptureRuntimeScope): Promise<boolean> {
    const list = await this.dependencies.storage.getList(listId);
    if (!list?.captureState) return false;
    if (scope && (list.userId !== scope.userId || list.module !== scope.module)) return false;

    this.countSync.reset();
    const engine = await this.loadEngine(listId, list);
    if (!engine) return false;
    // Restauração não é evidência de desaparecimento. A fase persistida permanece intacta;
    // somente eventos observáveis (por exemplo `search_tab_closed`) podem mudá-la.
    const step = engine.next();
    if (!(await this.dependencies.storage.setListCaptureState(listId, engine.snapshot()))) {
      this.engine = null;
      return false;
    }
    this.projectEngine(engine, {
      module: engine.module,
      count: list.count,
      countModule: engine.module,
      activeListId: listId,
      pauseReason: engine.pauseReason ?? null,
      progress: engine.progress,
      page: step?.position ?? engine.total,
      pages: engine.total,
    });
    return true;
  }

  async resume(listId: string): Promise<boolean> {
    const engine = await this.loadEngine(listId);
    if (!engine || engine.finished) return false;

    engine.resume();
    const step = engine.next();
    if (!step) return false;
    if (!(await this.dependencies.storage.setListCaptureState(listId, engine.snapshot()))) {
      this.engine = null;
      return false;
    }

    this.projectEngine(engine, {
      module: engine.module,
      activeListId: listId,
      pauseReason: null,
      progress: engine.progress,
      page: step.position,
      pages: engine.total,
    });
    const routingSnapshot = {
      play: true,
      captureTarget: 'panel',
      module: engine.module,
      captureListId: listId,
      captureRunId: engine.runId,
    };
    try {
      await this.dependencies.bridge.commitSync(routingSnapshot);
    } catch {
      engine.pause('extension_unavailable');
      await this.dependencies.storage.setListCaptureState(listId, engine.snapshot());
      this.projectEngine(engine, {
        activeListId: listId,
        pauseReason: 'extension_unavailable',
      });
      return false;
    }
    this.sendNavigation(engine, step);
    const stats = this.statsFor(engine);
    const pausedMs = stats.pausedAt === null ? undefined : Math.max(0, this.activityNow() - stats.pausedAt);
    stats.pausedAt = null;
    this.emit('capture.resumed', engine, { pausedMs });
    return true;
  }

  async pause(listId: string, reason?: CapturePauseReason): Promise<boolean> {
    return this.pauseExpectedRun(listId, undefined, reason);
  }

  /**
   * "Parar a captura" da busca em tela cheia: encerra a execução sem rodar as pesquisas que
   * faltam. Os leads já gravados ficam na lista; lotes atrasados deste run viram `stale_run`.
   */
  async stop(listId: string): Promise<boolean> {
    const list = await this.dependencies.storage.getList(listId);
    const engine = await this.loadEngine(listId, list);
    if (!engine || !list) return false;
    try {
      await this.finish(listId, engine, list.count, 'stopped');
    } catch {
      this.engine = null;
      return false;
    }
    return true;
  }

  /**
   * Converte “Nova lista” em transição durável, antes de zerar qualquer projeção.
   * A lista atual e seus registros permanecem; somente sua permissão de receber lotes termina.
   */
  async prepareNewList(userId: string, module: ListModule): Promise<void> {
    return withCaptureAccountLock(userId, () => this.prepareNewListLocked(userId, module));
  }

  private async prepareNewListLocked(userId: string, module: ListModule): Promise<void> {
    const abandoned = await this.dependencies.storage.abandonListCaptures(
      userId,
      module,
      this.dependencies.now(),
    );
    const current = this.engine;
    const currentWasAbandoned = Boolean(current && abandoned.includes(current.listId));
    if (currentWasAbandoned) {
      this.countSync.reset();
      this.engine = null;
      this.dependencies.view.update({
        runId: null,
        phase: null,
        active: false,
        module,
        count: 0,
        countModule: module,
        activeListId: null,
        pauseReason: null,
        progress: 0,
        page: 0,
        pages: 0,
      });
      // O snapshot durável já foi invalidado. A projeção antiga não pode voltar a ser
      // restaurada enquanto a nova intenção aguarda o POST; a próxima captura sobrescreve tudo.
      this.dependencies.bridge.setSync({
        play: false,
        module,
        captureListId: '',
        captureRunId: '',
      });
    }
    for (const listId of abandoned) {
      void this.dependencies.backup.uploadList(listId).catch(() => {
        // D3: a falha não perde a lista; dirtyAt mantém a retomada automática.
      });
    }
  }

  /** Única porta para eventos externos que pretendem mudar a fase durável. */
  async handleRuntimeEvent(event: CaptureRuntimeEvent): Promise<boolean> {
    if (
      event.type === 'delivery_paused' &&
      (event.reason === 'panel_closed' || event.reason === 'delivery_unavailable')
    ) {
      // Timeout/ausência de ACK só pausa a fila. `panel_closed` permanece aceito para estados
      // emitidos antes do corte 3, mas nenhum dos dois diagnósticos decide a fase global.
      return false;
    }
    const reason =
      event.type === 'search_tab_closed'
        ? 'search_tab_closed'
        : event.type === 'delivery_paused'
        ? this.pauseReasonForDelivery(event.reason)
        : 'user_requested';
    return this.pauseExpectedRun(event.listId, event.runId, reason);
  }

  private async pauseExpectedRun(
    listId: string,
    expectedRunId?: string,
    reason?: CapturePauseReason,
  ): Promise<boolean> {
    const engine = await this.loadEngine(listId);
    if (!engine) return false;
    if (expectedRunId && engine.runId !== expectedRunId) return false;
    if (engine.phase === 'paused') return true;
    engine.pause(reason);
    if (!(await this.dependencies.storage.setListCaptureState(listId, engine.snapshot()))) {
      this.engine = null;
      return false;
    }
    this.projectEngine(engine, {
      activeListId: listId,
      pauseReason: reason ?? null,
    });
    this.statsFor(engine).pausedAt = this.activityNow();
    this.emit('capture.paused', engine, { reason: reason ?? 'user_requested' });
    // Invariante 5: a projeção só muda depois de a fase pausada estar confirmada no IndexedDB.
    this.dependencies.bridge.setSync({ play: false, captureRunId: engine.runId });
    // Pausa e parada são gatilhos D3. A rede fica fora do caminho da interação.
    void this.dependencies.backup.uploadList(listId).catch(() => {
      // `dirtyAt` preserva o trabalho mesmo se o disparo não conseguir iniciar.
    });
    if (reason && reason !== 'user_requested') {
      this.dependencies.view.notify('error', pauseMessage(reason));
    }
    return true;
  }

  async handleStored(event: StoredCaptureBatch): Promise<boolean> {
    const engine = await this.loadEngine(event.batch.listId);
    if (
      !engine ||
      engine.module !== event.batch.module ||
      (event.batch.runId !== undefined && event.batch.runId !== engine.runId)
    ) {
      return false;
    }

    // Todo lote pertence à pesquisa aberta: o runtime só navega para a próxima depois do lote
    // que conclui esta. Repetições do mesmo ACK chegam com `inserted: 0`.
    const module = engine.module;
    if (engine.phase === 'running') this.countBatch(engine, event);
    if (isRunModule(module)) {
      this.dependencies.runLog?.stored({
        runId: engine.runId,
        module,
        row: engine.peekCurrent()?.i ?? null,
        inserted: event.result.inserted,
        added: event.result.added,
        listCount: event.result.count,
      });
    }

    // Um lote que terminou de persistir depois da parada ainda atualiza o total, mas nunca move
    // o cursor. A fase durável, não `sync.play`, decide se a etapa pode avançar.
    if (engine.phase !== 'running') {
      this.projectEngine(engine, { count: event.result.count, countModule: engine.module });
      this.countSync.publish(engine.module, event.result.count, engine.runId);
      return true;
    }

    // Emails/phones/hunter entregam cada página sem encerrar a etapa. Campo ausente continua
    // conclusivo para a extensão 8.1 publicada, que não conhece o corte 2.
    if (event.batch.stepComplete === false) {
      this.projectEngine(engine, { count: event.result.count, countModule: engine.module });
      this.countSync.publish(engine.module, event.result.count, engine.runId);
      return true;
    }

    if (engine.hasAcknowledged(event.ackId)) {
      this.projectEngine(engine, { count: event.result.count, countModule: engine.module });
      this.countSync.publish(engine.module, event.result.count, engine.runId);
      return true;
    }

    const current = engine.next();
    if (!current || !engine.markCurrentCompleted(event.ackId)) return false;
    // `step` é a posição da etapa (1 = primeira), contando também os bairros próximos.
    this.emitStepCompleted(engine, current.position);
    const next = engine.next();

    if (!next) {
      try {
        await this.finish(event.batch.listId, engine, event.result.count);
      } catch (error) {
        this.engine = null;
        throw error;
      }
      return true;
    }

    try {
      if (
        !(await this.dependencies.storage.setListCaptureState(
          event.batch.listId,
          engine.snapshot(),
        ))
      ) {
        throw new Error('A lista ativa desapareceu ao persistir o schedule.');
      }
    } catch (error) {
      this.engine = null;
      throw error;
    }

    this.projectEngine(engine, {
      count: event.result.count,
      countModule: engine.module,
      pauseReason: null,
      progress: engine.progress,
      page: next.position,
      pages: engine.total,
    });
    this.countSync.publish(engine.module, event.result.count, engine.runId);
    this.sendNavigation(engine, next);
    return true;
  }

  async handleFailure(batch: CaptureBatch, code: CaptureAckErrorCode): Promise<void> {
    await this.recordRejectedBatch(batch, code);
    if (!isPauseReason(code)) return;
    const engine = await this.loadEngine(batch.listId);
    if (
      !engine ||
      engine.phase !== 'running' ||
      engine.module !== batch.module ||
      (batch.runId !== undefined && batch.runId !== engine.runId)
    ) {
      return;
    }
    try {
      await this.pauseExpectedRun(batch.listId, batch.runId, code);
    } catch {
      // A falha original continua sendo a resposta do ACK; exibir/pausar não pode mascará-la.
    }
  }

  reset(): void {
    this.countSync.reset();
    this.engine = null;
  }

  private async loadEngine(listId: string, storedList?: LocalList): Promise<CaptureEngine | null> {
    const list = storedList ?? (await this.dependencies.storage.getList(listId));
    if (!list?.captureState) return null;
    this.userId = list.userId;
    if (this.engine) {
      if (
        this.engine.listId === listId &&
        list.captureState.version === 2 &&
        list.captureState.runId === this.engine.runId
      ) {
        return this.engine;
      }
      if (this.engine.listId !== listId) {
        const current = await this.dependencies.storage.getList(this.engine.listId);
        if (
          current?.captureState?.version === 2 &&
          current.captureState.runId === this.engine.runId
        ) {
          return null;
        }
      }
      // Outra aba pode ter substituído a execução enquanto este runtime estava em memória.
      // O snapshot durável vence; a instância velha nunca bloqueia a restauração do run novo.
      this.engine = null;
    }
    const migration = migrateCaptureEngineSnapshot(
      list.captureState,
      this.dependencies.createRunId,
    );
    this.engine = new CaptureEngine(migration.snapshot);
    if (
      migration.migrated &&
      !(await this.dependencies.storage.setListCaptureState(listId, this.engine.snapshot()))
    ) {
      this.engine = null;
    }
    return this.engine;
  }

  private pauseReasonForDelivery(
    reason: Extract<CaptureRuntimeEvent, { type: 'delivery_paused' }>['reason'],
  ): CapturePauseReason {
    if (
      reason === 'email_dns_unavailable' ||
      reason === 'quota_exceeded'
    ) {
      return reason;
    }
    // A fila conserva o diagnóstico detalhado. A fase global usa uma categoria estável sem
    // inventar que o painel fechou ou que os dados se perderam.
    return 'delivery_failed';
  }

  private activityNow(): number {
    return this.dependencies.activity?.now?.() ?? Date.now();
  }

  private statsFor(engine: CaptureEngine): RunActivityStats {
    let stats = this.runStats.get(engine.runId);
    if (!stats) {
      // Depois de recarregar a página, a etapa aberta recomeça a contar daqui.
      stats = {
        stepStartedAt: this.activityNow(),
        stepFound: 0,
        stepFresh: 0,
        stepPages: 0,
        leadsNew: 0,
        pausedAt: engine.phase === 'paused' ? this.activityNow() : null,
        acks: new Set(),
        rejected: new Set(),
      };
      this.runStats.set(engine.runId, stats);
    }
    return stats;
  }

  private emit(name: ActivityEventName, engine: CaptureEngine, props: Record<string, unknown>, step?: number): void {
    try {
      this.dependencies.activity?.emit(name, props, {
        search: engine.listId,
        run: engine.runId,
        ...(step !== undefined ? { step } : {}),
      });
    } catch {
      // O registro de uso nunca interfere na captura.
    }
  }

  /** Soma o lote na etapa aberta; o mesmo ACK repetido não conta de novo. */
  private countBatch(engine: CaptureEngine, event: StoredCaptureBatch): void {
    const stats = this.statsFor(engine);
    if (stats.acks.has(event.ackId)) return;
    stats.acks.add(event.ackId);
    const items = event.batch.module === 'places' ? event.batch.places : event.batch.leads;
    stats.stepFound += Array.isArray(items) ? items.length : 0;
    stats.stepFresh += Math.max(0, event.result.inserted);
    stats.stepPages += 1;
  }

  private emitStepCompleted(engine: CaptureEngine, step: number): void {
    const stats = this.statsFor(engine);
    const now = this.activityNow();
    this.emit(
      'capture.step_completed',
      engine,
      {
        outcome: stats.stepFound > 0 ? 'ok' : 'empty',
        found: stats.stepFound,
        fresh: stats.stepFresh,
        pages: stats.stepPages,
        durationMs: Math.max(0, now - stats.stepStartedAt),
      },
      step,
    );
    stats.leadsNew += stats.stepFresh;
    stats.stepFound = 0;
    stats.stepFresh = 0;
    stats.stepPages = 0;
    stats.acks.clear();
    stats.stepStartedAt = now;
  }

  /** Um evento por tipo de recusa e execução: um lote ruim repetido não enche o pool. */
  private async recordRejectedBatch(batch: CaptureBatch, code: CaptureAckErrorCode): Promise<void> {
    try {
      const engine = await this.loadEngine(batch.listId);
      if (!engine || (batch.runId !== undefined && batch.runId !== engine.runId)) return;
      const stats = this.statsFor(engine);
      if (stats.rejected.has(code)) return;
      stats.rejected.add(code);
      this.emit('capture.batch_rejected', engine, { error: code });
    } catch {
      // O registro de uso nunca interfere na resposta do ACK.
    }
  }

  private sendNavigation(engine: CaptureEngine, step: CaptureStep, beginCapture = false): void {
    this.statsFor(engine).stepStartedAt = this.activityNow();
    this.dependencies.bridge.sendRuntime({
      url: step.url,
      active: true,
      // Etapa do schedule: a extensão mostra "2/10" na camada da busca, como no fluxo legado.
      page: step.position,
      pages: step.total,
      ...(beginCapture ? { beginCapture: true } : {}),
      captureRunId: engine.runId,
      // Opt-in vivo e vinculado ao run: extensão/painel antigos continuam no protocolo anterior.
      captureProtocol: CAPTURE_EVENTS_PROTOCOL,
      // Velocidade do plano: no "Normal", a extensão anda à metade (decisão 299).
      captureSpeed: this.dependencies.speed?.() ?? 1,
    });
  }

  private projectEngine(engine: CaptureEngine, patch: Partial<CaptureRuntimeViewState> = {}): void {
    const module = engine.module;
    if (isRunModule(module) && !engine.finished) {
      const snapshot = engine.snapshot();
      this.dependencies.runLog?.sync({
        runId: engine.runId,
        listId: engine.listId,
        userId: this.userId,
        module,
        schedule: snapshot.schedule,
        current: engine.peekCurrent()?.i ?? null,
        ...(patch.count !== undefined ? { listCount: patch.count } : {}),
      });
    }
    this.dependencies.view.update({
      ...patch,
      runId: engine.runId,
      phase: engine.phase,
      active: engine.phase === 'running' && !engine.finished,
      module: engine.module,
      activeListId: engine.finished ? null : engine.listId,
      pauseReason: engine.pauseReason ?? null,
      progress: engine.progress,
      pages: engine.total,
    });
  }

  private async finish(
    listId: string,
    engine: CaptureEngine,
    count?: number,
    outcome: 'finished' | 'stopped' = 'finished',
  ): Promise<void> {
    if (!(await this.dependencies.storage.finishListCapture(listId, engine.runId))) {
      throw new Error('A lista ativa desapareceu ao concluir o schedule.');
    }
    const finalCount = count ?? 0;
    const stats = this.statsFor(engine);
    this.emit('capture.completed', engine, {
      outcome,
      stepsDone: engine.completed,
      stepsTotal: engine.total,
      leadsNew: stats.leadsNew,
    });
    this.runStats.delete(engine.runId);
    const module = engine.module;
    if (isRunModule(module)) {
      this.dependencies.runLog?.end({
        runId: engine.runId,
        module,
        outcome,
        listCount: finalCount,
      });
    }
    this.dependencies.view.update({
      runId: null,
      phase: null,
      active: false,
      count: finalCount,
      countModule: engine.module,
      activeListId: null,
      pauseReason: null,
      progress: 100,
      page: engine.total,
      pages: engine.total,
    });
    // Fura o throttle numa única escrita: a aba observa `play=false` somente com o total final.
    this.countSync.finish(engine.module, finalCount, engine.runId);
    // Parar é um gesto da própria pessoa: a tela já mostra o resumo, sem aviso extra.
    if (outcome === 'finished') this.dependencies.view.notify('success', 'Captura concluída.');
    this.engine = null;

    // O ACK do último lote não espera rede: o dado já está confirmado no IndexedDB e continua
    // com `dirtyAt` até todos os chunks subirem. O motor também retoma no boot/online/pausa.
    void this.dependencies.backup.uploadList(listId).catch(() => {
      // Uma falha inesperada também é segura: `finishListCapture` já deixou a lista suja.
    });
  }
}

let defaultRuntime = new CaptureRuntime();
let runtimeTransitionTail: Promise<unknown> = Promise.resolve();

/**
 * Todos os comandos do singleton passam pela mesma corrente. ACK e parada concorrentes deixam de
 * competir pelo snapshot: o runtime sempre termina uma transição persistida antes da próxima.
 */
function serializeRuntimeTransition<T>(operation: () => Promise<T>): Promise<T> {
  const result = runtimeTransitionTail.then(operation, operation);
  runtimeTransitionTail = result.catch(() => undefined);
  return result;
}

export function startCaptureRuntime(options: StartCaptureRuntimeOptions): Promise<void> {
  return serializeRuntimeTransition(() => defaultRuntime.start(options));
}

export function restoreCaptureRuntime(
  listId: string,
  scope?: CaptureRuntimeScope,
): Promise<boolean> {
  return serializeRuntimeTransition(() => defaultRuntime.restore(listId, scope));
}

export function prepareNewListCaptureRuntime(
  userId: string,
  module: ListModule,
): Promise<void> {
  return serializeRuntimeTransition(() => defaultRuntime.prepareNewList(userId, module));
}

export function resumeCaptureRuntime(listId: string): Promise<boolean> {
  return serializeRuntimeTransition(() => defaultRuntime.resume(listId));
}

export function pauseCaptureRuntime(
  listId: string,
  reason?: CapturePauseReason,
): Promise<boolean> {
  return serializeRuntimeTransition(() => defaultRuntime.pause(listId, reason));
}

export function stopCaptureRuntime(listId: string): Promise<boolean> {
  return serializeRuntimeTransition(() => defaultRuntime.stop(listId));
}

export function handleStoredCaptureBatch(event: StoredCaptureBatch): Promise<boolean> {
  return serializeRuntimeTransition(() => defaultRuntime.handleStored(event));
}

export function handleCaptureIngestFailure(
  batch: CaptureBatch,
  code: CaptureAckErrorCode,
): Promise<void> {
  return serializeRuntimeTransition(() => defaultRuntime.handleFailure(batch, code));
}

export function handleCaptureRuntimeEvent(event: CaptureRuntimeEvent): Promise<boolean> {
  return serializeRuntimeTransition(() => defaultRuntime.handleRuntimeEvent(event));
}

export function __resetCaptureRuntime(): void {
  defaultRuntime.reset();
  defaultRuntime = new CaptureRuntime();
  runtimeTransitionTail = Promise.resolve();
}
